import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'react-hot-toast';
import { Plus, Trash2, ShieldHalf } from 'lucide-react';
import { rbacApi } from '../../services/rbacApi';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * User Permissions — ERPNext-style record-level scoping. Restrict a user to
 * specific record values of a DocType (e.g. employee = EMP-001 → they only
 * see their own employee record / can use the Self-Service Portal).
 */
const UserPermissionsTab = () => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [doctypes, setDoctypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ username: '', doctype: '', value: '' });

  const load = () => {
    rbacApi.listUserPermissions().then((r) => setRows(r.data)).catch(() => toast.error('Failed to load user permissions'));
    rbacApi.listUsers().then((r) => setUsers(r.data));
    rbacApi.doctypes().then((r) => setDoctypes(r.data));
  };
  useEffect(load, []);

  // Load employees for a friendly value dropdown when doctype = employee.
  useEffect(() => {
    if (form.doctype === 'employee' && employees.length === 0) {
      axios.get(`${API}/api/hcm/employees?limit=500`)
        .then((r) => setEmployees(r.data || []))
        .catch(() => {});
    }
  }, [form.doctype, employees.length]);

  const add = async () => {
    if (!form.username || !form.doctype || !form.value) { toast.error('User, DocType and value are required'); return; }
    try {
      await rbacApi.createUserPermission(form);
      toast.success('User permission added');
      setForm({ username: '', doctype: '', value: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    try { await rbacApi.deleteUserPermission(id); toast.success('Removed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="user-permissions-tab">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4 text-sm text-orange-800">
        Restrict a user to specific records of a DocType (record-level scoping). Example: link a portal user to
        their own <strong>employee</strong> record so they only see their own profile. Leave a DocType unrestricted
        and the user sees all records they have role access to.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mb-4">
        <div>
          <Label>User</Label>
          <Select value={form.username} onValueChange={(v) => setForm({ ...form, username: v })}>
            <SelectTrigger data-testid="up-user-select"><SelectValue placeholder="Select user" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {users.map((u) => <SelectItem key={u.user_id} value={u.username}>{u.username}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>DocType</Label>
          <Select value={form.doctype} onValueChange={(v) => setForm({ ...form, doctype: v, value: '' })}>
            <SelectTrigger data-testid="up-doctype-select"><SelectValue placeholder="Select DocType" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {doctypes.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Allowed value</Label>
          {form.doctype === 'employee' ? (
            <Select value={form.value} onValueChange={(v) => setForm({ ...form, value: v })}>
              <SelectTrigger data-testid="up-value-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {employees.map((e) => (
                  <SelectItem key={e.employee_id} value={e.employee_id}>
                    {e.employee_id} — {e.first_name} {e.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
                   placeholder="Record primary key" data-testid="up-value-input" />
          )}
        </div>
        <Button className="bg-orange-600 hover:bg-orange-700" onClick={add} data-testid="up-add-btn">
          <Plus className="w-4 h-4 mr-1" />Add
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-2 text-left">User</th>
              <th className="px-4 py-2 text-left">DocType</th>
              <th className="px-4 py-2 text-left">Allowed Value</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                <ShieldHalf className="w-6 h-6 mx-auto mb-1 text-gray-300" />No user permissions — all users are unrestricted (subject to roles).
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.user_permission_id} className="hover:bg-gray-50" data-testid={`up-row-${r.username}-${r.value}`}>
                <td className="px-4 py-2 font-medium">{r.username}</td>
                <td className="px-4 py-2">{r.doctype}</td>
                <td className="px-4 py-2">{r.value}</td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(r.user_permission_id)} data-testid={`up-delete-${r.username}-${r.value}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserPermissionsTab;
